import React, { useEffect, useState } from 'react';
import { countriesAPI } from '../api';
import toast from 'react-hot-toast';
import {
    Search,
    Plus,
    Edit2,
    Trash2,
    X,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';

const defaultForm = {
    country_name: '',
    country_code: '',
    currency_name: '',
    currency_symbol: '',
    country_flag: '',
    telephone_code: '',
    language_name: '',
    language_code: '',
};

export default function Countries() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [modal, setModal] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const limit = 10;

    const fetchCountries = () => {
        setLoading(true);
        countriesAPI
            .getAll({ page, limit, search })
            .then((res) => {
                setCountries(res.data.data.countries);
                setTotal(res.data.data.total);
            })
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCountries();
    }, [page]);
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            fetchCountries();
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const openCreate = () => {
        setForm(defaultForm);
        setEditId(null);
        setModal(true);
    };
    const openEdit = (c) => {
        setForm({ ...c });
        setEditId(c.country_id);
        setModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editId) {
                await countriesAPI.update(editId, form);
                toast.success('Country updated');
            } else {
                await countriesAPI.create(form);
                toast.success('Country created');
            }
            setModal(false);
            fetchCountries();
        } catch {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await countriesAPI.delete(id);
            toast.success('Deleted');
            fetchCountries();
        } catch {
            toast.error('Failed to delete');
        }
    };

    const pages = Math.ceil(total / limit);

    return (
        <div className="fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Countries</h1>
                    <p className="page-subtitle">
                        {total} countries configured
                    </p>
                </div>
                <button className="btn btn-primary" onClick={openCreate}>
                    <Plus size={16} /> Add Country
                </button>
            </div>

            <div className="filters">
                <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
                    <Search size={16} />
                    <input
                        className="input"
                        placeholder="Search countries..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="card" style={{ padding: 0 }}>
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Country</th>
                                <th>Code</th>
                                <th>Currency</th>
                                <th>Phone Code</th>
                                <th>Language</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6}>
                                        <div className="loading-container">
                                            <div className="spinner" />
                                        </div>
                                    </td>
                                </tr>
                            ) : countries.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        style={{
                                            textAlign: 'center',
                                            color: 'var(--text3)',
                                            padding: 60,
                                        }}
                                    >
                                        No countries found
                                    </td>
                                </tr>
                            ) : (
                                countries.map((c) => (
                                    <tr key={c.country_id}>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                }}
                                            >
                                                {/*                       {c.country_flag && <span style={{ fontSize: 20 }}>{c.country_flag}</span>}
                                                 */}{' '}
                                                <span
                                                    style={{
                                                        color: 'var(--text)',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {c.country_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="tag">
                                                {c.country_code}
                                            </span>
                                        </td>
                                        <td>
                                            {c.currency_name} (
                                            {c.currency_symbol})
                                        </td>
                                        <td>+{c.telephone_code}</td>
                                        <td>
                                            {c.language_name} ({c.language_code}
                                            )
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 8,
                                                }}
                                            >
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={() => openEdit(c)}
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() =>
                                                        handleDelete(
                                                            c.country_id,
                                                            c.country_name
                                                        )
                                                    }
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div
                        style={{
                            padding: '16px 20px',
                            borderTop: '1px solid var(--border)',
                        }}
                    >
                        <div className="pagination">
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeft size={14} />
                            </button>
                            {Array.from(
                                { length: Math.min(pages, 5) },
                                (_, i) => i + 1
                            ).map((p) => (
                                <button
                                    key={p}
                                    className={`page-btn ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page === pages}
                            >
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <div
                    className="modal-overlay"
                    onClick={(e) =>
                        e.target === e.currentTarget && setModal(false)
                    }
                >
                    <div className="modal">
                        <div className="modal-header">
                            <h2 className="modal-title">
                                {editId ? 'Edit Country' : 'Add Country'}
                            </h2>
                            <button
                                onClick={() => setModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text3)',
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="grid-2">
                                {[
                                    {
                                        key: 'country_name',
                                        label: 'Country Name',
                                        required: true,
                                    },
                                    {
                                        key: 'country_code',
                                        label: 'Country Code (e.g. IN)',
                                        required: true,
                                    },
                                    {
                                        key: 'currency_name',
                                        label: 'Currency Name',
                                        required: true,
                                    },
                                    {
                                        key: 'currency_symbol',
                                        label: 'Currency Symbol',
                                        required: true,
                                    },
                                    {
                                        key: 'country_flag',
                                        label: 'Flag Emoji',
                                        required: false,
                                    },
                                    {
                                        key: 'telephone_code',
                                        label: 'Phone Code (e.g. 91)',
                                        required: true,
                                    },
                                    {
                                        key: 'language_name',
                                        label: 'Language Name',
                                        required: true,
                                    },
                                    {
                                        key: 'language_code',
                                        label: 'Language Code (e.g. en)',
                                        required: true,
                                    },
                                ].map(({ key, label, required }) => (
                                    <div className="form-group" key={key}>
                                        <label>{label}</label>
                                        <input
                                            className="input"
                                            placeholder={label}
                                            value={form[key]}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    [key]: e.target.value,
                                                })
                                            }
                                            required={required}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    justifyContent: 'flex-end',
                                    marginTop: 8,
                                }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <div
                                            className="spinner"
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderWidth: 2,
                                            }}
                                        />
                                    ) : editId ? (
                                        'Update'
                                    ) : (
                                        'Create'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
